import type React from 'react';
import { Table, Column } from '../../../components/ui/Table';
import { Machine } from './presenter';
import { CalendarDays, CalendarRange, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';

export const DashboardView: React.FC<{
  machines: Machine[];
  loading: boolean;
  error: string | null;
  summaries: { day: any; week: any; month: any };
  productCounts?: { active: number; inactive: number };
  onMachineClick?: (m: Machine) => void;
  rowDetail?: (m: Machine) => React.ReactNode;
}> = ({ machines, loading, error, summaries, productCounts: _productCounts, onMachineClick: _onMachineClick, rowDetail }) => {
  const columns: Column<Machine>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (m) => (
        <span>{m.name}</span>
      ),
    },
    { key: 'location', header: 'Location' },
    {
      key: 'status',
      header: 'Status',
      render: (m) => (
        <span className={(m.status === 'online'
          ? 'inline-flex items-center rounded-full bg-green-500 px-2 py-0.5 text-xs font-medium text-white'
          : 'inline-flex items-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-medium text-white')}>{m.status}</span>
      ),
    },
    {
      key: 'lastSyncAt',
      header: 'Last Sync',
      render: (m) => m.lastSyncAt ? new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(m.lastSyncAt)) : '-'
    },
  ];

  const formatIDR = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n || 0);

  const sortedMachines = useMemo(() => {
    if (!machines || machines.length === 0) return [];
    return [...machines].sort((a, b) => {
      // 1. Online status comes first
      if (a?.status === 'online' && b?.status !== 'online') return -1;
      if (a?.status !== 'online' && b?.status === 'online') return 1;

      // 2. Safely grab names and convert to lowercase for accurate alphabetical sorting
      const nameA = String(a?.name || '').toLowerCase();
      const nameB = String(b?.name || '').toLowerCase();

      // Sort alphabetically by name
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      
      return 0;
    });
  }, [machines]);

  return (
    <div className="p-6 space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard Overview</h1>
        <p className="text-muted-foreground">Welcome back. Here's what's happening with your machines today.</p>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight">Transactions</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SummaryCard title="Transactions (Day)" value={summaries.day.transactions} icon={<CalendarDays className="h-5 w-5 text-primary" />} />
          <SummaryCard title="Transactions (Week)" value={summaries.week.transactions} icon={<CalendarRange className="h-5 w-5 text-primary" />} />
          <SummaryCard title="Transactions (Month)" value={summaries.month.transactions} icon={<TrendingUp className="h-5 w-5 text-primary" />} />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight">Revenue</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SummaryCard title="Revenue (Day)" value={formatIDR(summaries.day.revenue)} icon={<CalendarDays className="h-5 w-5 text-green-600 dark:text-green-400" />} />
          <SummaryCard title="Revenue (Week)" value={formatIDR(summaries.week.revenue)} icon={<CalendarRange className="h-5 w-5 text-green-600 dark:text-green-400" />} />
          <SummaryCard title="Revenue (Month)" value={formatIDR(summaries.month.revenue)} icon={<TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />} />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">{error}</div>
      )}

      <div className="rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden">
        <div className="p-6 border-b bg-muted/20">
          <h3 className="text-lg font-semibold leading-none tracking-tight">Machines</h3>
          <p className="text-sm text-muted-foreground mt-2">Monitor your vending machines.</p>
        </div>
        <div className="p-0">
          <Table
            columns={columns}
            data={sortedMachines}
            page={1}
            pageSize={sortedMachines.length || 10}
            total={sortedMachines.length}
            onPageChange={() => { }}
            rowDetail={rowDetail}
            getRowClass={(m) => m.status === 'online' ? 'hover:bg-green-50/50 dark:hover:bg-green-950/20 transition-colors' : 'bg-red-50/50 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors'}
          />
        </div>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ title: string; value: React.ReactNode; icon?: React.ReactNode }> = ({ title, value, icon }) => (
  <div className="group flex items-center justify-between rounded-xl border bg-card/50 backdrop-blur-sm p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-card">
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">{title}</div>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
    </div>
    {icon && (
      <div className="ml-4 rounded-full bg-primary/10 p-3 transition-colors duration-300 group-hover:bg-primary/20">
        {icon}
      </div>
    )}
  </div>
);

export default DashboardView;
